import { ButcherHeader } from '@components/ButcherHeader';
import { SideNav } from '@components/OrderCreation/OrderSideNav';
import {
  CheckinOrderProps,
  HuntingLicenseProps,
  NewOrderInitiationProps,
  RequireFlag,
  Species,
} from '@helpers/Models';
import { useEffect, useState } from 'react';
import { useInputValidation } from '@hooks/useInputValidation';
import {
  errorMessage,
  orderBreadCrumbSteps,
  orderStepper,
} from '@helpers/Constants';
import { FrontendRoutes } from '@helpers/Constants/Navigation';
import { useToast } from '@contexts/Toast';
import { OrderStepper } from '@components/OrderCreation/OrderStepper';
import { OrderBreadcrumb } from '@components/OrderCreation/OrderBreadcrumbs';
import { OrderProfileCard } from '@components/OrderCreation/OrderProfileCard';
import { useNavigate } from 'react-router-dom';
import { CustomerOrderService } from '@services/CustomerService';
import { LicenseDetails } from '@components/Modal/LicenseDetails/LicenseDetails';
import { formatDate } from '@services/OrderCreationService';

// ----- Custom Dropdown Component (only for license number) -----
interface CustomSelectOption {
  value: number;
  label: string;
}

interface CustomSelectProps {
  options: CustomSelectOption[];
  value: number | string;
  onChange: (value: number) => void;
  isDisabled?: boolean;
  placeholder?: string;
  className?: string;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  options,
  value,
  onChange,
  isDisabled = false,
  placeholder = 'Select license number',
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const selectedOption = options.find((opt) => opt.value === Number(value));

  const handleSelect = (val: number) => {
    onChange(val);
    setIsOpen(false);
  };

  return (
    <div className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => !isDisabled && setIsOpen(!isOpen)}
        disabled={isDisabled}
        className={`w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-left focus:outline-primary outline-gray-300 ${
          isDisabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
        }`}
      >
        {selectedOption ? selectedOption.label : placeholder}
      </button>
      {isOpen && !isDisabled && (
        <ul className="absolute z-10 w-full mt-1 max-h-60 overflow-auto bg-white border border-gray-300 rounded-md shadow-lg">
          {options.map((opt) => (
            <li
              key={opt.value}
              onClick={() => handleSelect(opt.value)}
              className={`px-3 py-2 text-sm cursor-pointer hover:bg-primary-50 ${
                Number(value) === opt.value ? 'bg-primary-100 font-medium' : ''
              }`}
            >
              <span className="break-words whitespace-normal">{opt.label}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ----- Main Component -----
export const OrderCheckin: React.FC = () => {
  const { showToastMessage } = useToast();
  const navigate = useNavigate();
  const [checkinOrderData, setCheckinOrderData] = useState<CheckinOrderProps>({
    weight: '',
    permitNumber: '',
    citesNumber: '',
    conformationNumber: '',
    notes: '',
    huntingLicenceId: 0,
    feet: 0,
    inches: 0,
    licenseNumber: '',
    harvestStateId: 0,
  });
  const [customerId, setCustomerId] = useState<number>(0);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [speciesId, setSpeciesId] = useState<number>();
  const [currentCustomizingId, setCurrentCustomizingId] = useState<string>('');

  const [requireFlag, setRequireFlag] = useState<RequireFlag>({
    citesFlag: false,
    PermitFlag: false,
    LicenseFlag: false,
    LengthFlag: false,
  });

  const [customerDetails, setCustomerDetails] =
    useState<NewOrderInitiationProps>();
  const [showLicenseForm, setShowLicenseForm] = useState<boolean>(false);

  // Input validation hooks for feet and inches
  const feetValidation = useInputValidation('number', '');
  const inchesValidation = useInputValidation('number', '');

  /**
   * Refreshes customer details from localStorage.
   * This is useful when customer data is updated (e.g., new license added)
   * and we need to refresh the component state.
   */
  const refreshCustomerDetails = () => {
    const updatedCustomerDetails = CustomerOrderService.getCustomerDetails();
    setCustomerDetails(updatedCustomerDetails);
  };

  /**
   * Effect hook that runs once on component mount.
   *
   * - Retrieves customer details from localStorage and sets the customer ID in state.
   * - If no customer data is found, redirects the user to the customer list page.
   * - Triggers the initial fetch of available states.
   */
  useEffect(() => {
    const customerDetails = CustomerOrderService.getCustomerDetails();

    setCustomerDetails(customerDetails);
    if (customerDetails.id) {
      setCustomerId(customerDetails.id);

      const currentCustomizingId = localStorage.getItem('currentCustomizingId');
      if (currentCustomizingId) {
        setCurrentCustomizingId(currentCustomizingId);
      }
    } else {
      navigate(FrontendRoutes.CUSTOMER_LIST);
    }
  }, []);

  useEffect(() => {
    if (speciesId !== undefined) {
      const speciesData = localStorage.getItem('speciesData');
      const species = JSON.parse(speciesData ?? '[]').find(
        (s: Species) => s.id === speciesId,
      );

      if (species) {
        setRequireFlag({
          citesFlag: species.requireCitesFlag == 1,
          PermitFlag: species.requirePermitFlag == 1,
          LicenseFlag: species.requireLicenseFlag == 1,
          LengthFlag: species.requireLengthFlag == 1,
        });
      }
    }
  }, [speciesId]);

  useEffect(() => {
    if (customerId) {
      getOrderData();
    }
  }, [customerId]);

  /**
   * Retrieves and parses the `orderData` object from localStorage.
   *
   * @function
   * @returns {Record<string, any>} The parsed order data object.
   * Returns an empty object if no data is found in localStorage.
   */
  const getParsedOrderData = (): Record<string, any> => {
    const stored = localStorage.getItem('orderData');
    return stored ? JSON.parse(stored) : {};
  };

  /**
   * Retrieves order data for the current customer from localStorage.
   *
   * - Parses the `orderData` object from localStorage.
   * - Extracts and sets the order data specific to the current `customerId`.
   * - Sets the associated `speciesId` from the retrieved customer order.
   * - If check-in data exists for the species, it sets `checkinOrderData` to prefill the form.
   *
   * @returns {void}
   */
  const getOrderData = (): void => {
    const orderData = getParsedOrderData();

    if (orderData && customerId) {
      const customerOrder = orderData[customerId];

      // if customer order is undefned, means check in data is not available for the species, then create new
      // setOrderData(customerOrder);
      setSpeciesId(customerOrder.speciesId);

      // If check-in data exists, prepopulate it
      const existingCheckin =
        customerOrder?.species?.[currentCustomizingId]?.checkIn;

      // Prepopulate the feet and inches
      if (existingCheckin) {
        feetValidation.setValue(existingCheckin.feet ?? '');
        inchesValidation.setValue(existingCheckin.inches ?? '');
        setCheckinOrderData({ ...existingCheckin });
      } else {
        feetValidation.setValue('');
        inchesValidation.setValue('');
      }

      if (existingCheckin) {
        setCheckinOrderData({
          ...existingCheckin,
        });
      }
    }
  };

  /**
   * Validates a specific form field based on its name and value.
   *
   * - Checks if required fields are non-empty.
   * - Applies conditional validation for `permitNumber` and `citesNumber` based on requirement flags.
   * - Ensures `stateId` is not empty or zero.
   * - Validates `notes` length does not exceed 200 characters.
   * - Updates the error state for the given field.
   *
   * @function validateField
   * @param {string} field - The name of the form field to validate.
   * @param {string} value - The current value of the form field.
   * @returns {void}
   */
  const validateField = (field: string, value: string): void => {
    let error = '';

    switch (field) {
      case 'feet':
        if (
          requireFlag.LengthFlag &&
          (!value.trim() || parseInt(value || '0') === 0)
        ) {
          error = errorMessage.FIELD_REQUIRED;
        }
        break;
      case 'inches':
        if (
          requireFlag.LengthFlag &&
          (!value.trim() || parseInt(value || '0') === 0)
        ) {
          error = errorMessage.FIELD_REQUIRED;
        }
        break;
      case 'weight':
        if (!value.trim()) {
          error = errorMessage.FIELD_REQUIRED;
        }
        break;
      case 'huntingLicenceId':
        if (
          requireFlag.LicenseFlag &&
          (!value.trim() || parseInt(value) === 0)
        ) {
          error = errorMessage.FIELD_REQUIRED;
        }
        break;
      case 'licenseNumber':
        if (requireFlag.LicenseFlag && !value.trim()) {
          error = errorMessage.FIELD_REQUIRED;
        }
        break;
      case 'permitNumber':
        if (requireFlag.PermitFlag && !value.trim()) {
          error = errorMessage.FIELD_REQUIRED;
        }
        break;
      case 'citesNumber':
        if (requireFlag.citesFlag && !value.trim()) {
          error = errorMessage.FIELD_REQUIRED;
        }
        break;
      case 'notes':
        if (value.trim() && value.length > 200) {
          error = errorMessage.NOTES_EXEEDS_200_CHARECTER;
        }
        break;
    }
    setErrors((prevErrors) => ({
      ...prevErrors,
      [field]: error,
    }));
  };

  /**
   * Handles changes to form inputs and updates the state.
   *
   * - Updates the `checkinOrderData` state with the new value for the specified field.
   * - If the `stateId` field is changed and requires a license, it triggers the license verification process.
   * - Validates the field value immediately after updating the state.
   *
   * @async
   * @function handleInputChange
   * @param {React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>} e - The change event triggered by the form input.
   * @returns {Promise<void>}
   */
  const handleInputChange = async (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ): Promise<void> => {
    const { name, value } = e.target;

    // Handle feet and inches with validation hooks
    if (name === 'feet') {
      feetValidation.handleChange(e as React.ChangeEvent<HTMLInputElement>);
      setCheckinOrderData((prevData) => ({
        ...prevData,
        [name]: value === '' ? 0 : Number(value),
      }));
      validateField(name, value);
      return;
    }

    if (name === 'inches') {
      inchesValidation.handleChange(e as React.ChangeEvent<HTMLInputElement>);
      setCheckinOrderData((prevData) => ({
        ...prevData,
        [name]: value === '' ? 0 : Number(value),
      }));
      validateField(name, value);
      return;
    }

    const fieldsWithNumbers = ['huntingLicenceId'];
    const finalValue = fieldsWithNumbers.includes(name) ? Number(value) : value;

    setCheckinOrderData((prevData) => ({
      ...prevData,
      [name]: finalValue,
    }));

    validateField(name, value);
  };

  /**
   * Handles validation, data persistence, and navigation after the check-in step.
   *
   * This function performs the following:
   * - Validates required fields in `checkinOrderData` (e.g. `stateId`, `length`, `weight`, and optionally `permitNumber`, `citesNumber`).
   * - If validation fails, displays a toast error message and exits early.
   * - Retrieves and parses the existing `orderData` from `localStorage`.
   * - Ensures the `species` array exists and is correctly sized based on `currentCustomizingId`.
   * - Updates the current species entry with:
   *   - A composed `speciesName` (with index suffix),
   *   - Preserved `services` data (if any),
   *   - The current `checkinOrderData`, ensuring `stateId` is stored as a number.
   * - Saves the updated order data back to `localStorage`.
   * - Navigates the user to the service selection screen (`CREATE_ORDER_SERVICE`).
   *
   * @returns {void}
   */
  const handleNext = (): void => {
    // Check if any required fields are empty
    if (requireFlag.PermitFlag && !checkinOrderData?.permitNumber) {
      return showToastMessage(errorMessage.FORM_REQUIRED, 'error');
    }

    if (requireFlag.citesFlag && !checkinOrderData?.citesNumber) {
      return showToastMessage(errorMessage.FORM_REQUIRED, 'error');
    }

    if (requireFlag.LicenseFlag) {
      if (!checkinOrderData?.huntingLicenceId) {
        return showToastMessage('Please select a species state', 'error');
      }
      if (!checkinOrderData?.licenseNumber) {
        return showToastMessage('Please select a license number', 'error');
      }
    }

    if (requireFlag.LengthFlag) {
      if (!checkinOrderData.feet || checkinOrderData.feet === 0) {
        return showToastMessage('Please enter feet value for length', 'error');
      }
      if (!checkinOrderData.inches || checkinOrderData.inches === 0) {
        return showToastMessage(
          'Please enter inches value for length',
          'error',
        );
      }
    }

    // Get the existing orderData from localStorage
    const orderDataPayload = getParsedOrderData();
    const speciesName = localStorage.getItem('speciesName');

    // Ensure species array exists
    if (!orderDataPayload[customerId].species) {
      orderDataPayload[customerId].species = [];
    }

    // Ensure the species array has enough items
    while (
      orderDataPayload[customerId].species.length <= currentCustomizingId
    ) {
      orderDataPayload[customerId].species.push({}); // Use empty object instead of null
    }

    // Safely get the current species object
    const currentSpecies =
      orderDataPayload[customerId].species[currentCustomizingId] || {};
    let updatedServices: unknown[] = [];
    if (currentSpecies && typeof currentSpecies.services !== 'undefined') {
      updatedServices = currentSpecies.services;
    } else {
      updatedServices = [];
    }

    // Add the existing species data if there's any
    const existingSpecies =
      orderDataPayload[customerId].species[currentCustomizingId] ?? {};

    // Sending null for the values 0 and " ".
    const checkIn = Object.fromEntries(
      Object.entries({
        ...checkinOrderData,
        feet: checkinOrderData.feet || null,
        inches: checkinOrderData.inches || null,
      }).map(([key, value]) => [
        key,
        value === 0 || value === '' ? null : value,
      ]),
    );

    orderDataPayload[customerId].species[currentCustomizingId] = {
      speciesName: `${speciesName} - ${Number(currentCustomizingId) + 1}`,
      ...existingSpecies,
      checkIn,
      services: updatedServices,
    };

    // Save to localStorage
    localStorage.setItem('orderData', JSON.stringify(orderDataPayload));

    // Navigate to next route
    navigate(FrontendRoutes.CREATE_ORDER_SERVICE);
  };

  /**
   * Extracts a unique list of states from a collection of hunting licenses.
   *
   * This function iterates over the provided `huntingLicences` array and builds
   * a list of unique state objects based on each license's `harvestStateId`.
   * Each returned state object contains an `id` (the harvest state ID)
   * and a `name` (the state's name from `HarvestState.stateName`).
   *
   * @param {HuntingLicenseProps[]} [huntingLicences=[]]
   *   An array of hunting license objects. Each license should include:
   *   - `harvestStateId`: number — The unique identifier of the harvest state.
   *   - `HarvestState`: { stateName?: string } — The associated state object (optional).
   *
   * @returns {{ id: number; name: string }[]}
   *   A deduplicated array of state objects, each with:
   *   - `id`: The harvest state ID.
   *   - `name`: The corresponding state name.
   *
   */
  const getStates = (huntingLicences: HuntingLicenseProps[] = []) => {
    return Array.from(
      new Map(
        huntingLicences.map((license) => [
          license.harvestStateId,
          {
            id: license.harvestStateId, // Store HarvestStateId as the ID for the state
            name: license.HarvestState?.stateName,
          },
        ]),
      ).values(),
    );
  };

  /**
   * Handles changes to the license number via the custom dropdown.
   *
   * @param {number} selectedLicenseId - The ID of the selected license.
   * @returns {void}
   */
  const handleLicenseNumberChange = (selectedLicenseId: number): void => {
    const selectedLicense = customerDetails?.HuntingLicences?.find(
      (license) => license.id === selectedLicenseId,
    );
    setCheckinOrderData((prevData) => ({
      ...prevData,
      huntingLicenceId: selectedLicenseId,
      licenseNumber: selectedLicense?.licenseNumber || '',
    }));
  };

  // Build options for the custom dropdown – ensures value is a number
  const licenseOptions = (customerDetails?.HuntingLicences ?? [])
    .filter(
      (license) =>
        license.harvestStateId === Number(checkinOrderData.harvestStateId) &&
        license.id != null,
    )
    .map((license) => ({
      value: license.id!,
      label: `${license.licenseNumber}  Expiry: ${formatDate(license.expiryDate!)}`,
    }));

  return (
    <div className="flex h-screen w-screen">
      {/* Sidebar */}
      <SideNav active={2} />
      {/* Main Content */}
      <div className="flex-1 overflow-auto relative">
        <div>
          {/* Header */}
          <ButcherHeader />
          {/* Breadcrumbs */}
          <OrderBreadcrumb
            steps={orderBreadCrumbSteps}
            customerName={`${customerDetails?.firstName} ${customerDetails?.lastName}`}
            activeItem="New Order"
            customerId={customerId}
          />
        </div>
        <div className="p-3 pb-16">
          {/* Customer Profile Card */}
          <OrderProfileCard />
          {/* Stepper */}
          <OrderStepper steps={orderStepper} currentStep={1} />
          <form className="space-y-4">
            <div className="flex justify-between mt-4 mb-3">
              <h2 className="text-lg font-medium">Check-In</h2>
            </div>
            <div className="space-y-2">
              {requireFlag.LicenseFlag && (
                <div className="border border-b-gray-400 p-4 rounded-lg shadow-md bg-[#FFFEFA]">
                  <div className="grid grid-cols-2 sm:grid-cols-2 gap-4 mt-2">
                    <div>
                      <label
                        htmlFor="harvestStateId"
                        className="block text-sm font-medium text-[#757575] mb-2"
                      >
                        Species State *
                      </label>
                      <select
                        name="harvestStateId"
                        value={Number(checkinOrderData.harvestStateId)}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm outline-gray-320 focus:outline-primary outline-gray-300"
                      >
                        <option value="">Select state</option>
                        {getStates(customerDetails?.HuntingLicences).map(
                          (state) => (
                            <option key={state.id} value={state.id}>
                              {state.name}
                            </option>
                          ),
                        )}
                      </select>

                      {errors.harvestStateId && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.harvestStateId}
                        </p>
                      )}
                      {errors.license && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.license}
                        </p>
                      )}
                    </div>
                    <div>
                      <label
                        htmlFor="huntingLicenceId"
                        className="block text-sm font-medium text-[#757575] mb-2"
                      >
                        License number *
                      </label>
                      {/* Custom dropdown with text wrapping */}
                      <CustomSelect
                        options={licenseOptions}
                        value={checkinOrderData.huntingLicenceId || ''}
                        onChange={handleLicenseNumberChange}
                        isDisabled={!checkinOrderData.harvestStateId}
                        placeholder="Select license number"
                        className="w-full"
                      />

                      {errors.huntingLicenceId && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.huntingLicenceId}
                        </p>
                      )}
                    </div>
                  </div>
                  <div id="add-license" className="mt-3">
                    <button
                      type="button"
                      onClick={() => setShowLicenseForm(true)}
                      className="w-full px-4 py-2 font-semibold rounded-md bg-white text-primary border border-primary hover:bg-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                    >
                      + Add or Edit License
                    </button>
                  </div>
                </div>
              )}
              {/* Flag details */}
              {(requireFlag.PermitFlag || requireFlag.citesFlag) && (
                <div className="border border-b-gray-400 p-4 rounded-lg shadow-md bg-[#FFFEFA]">
                  <div className="grid grid-cols-2 sm:grid-cols-2 gap-4 mt-2">
                    {requireFlag.PermitFlag && (
                      <div>
                        <label
                          htmlFor="permitNumber"
                          className="block text-sm font-medium text-[#757575] mb-2"
                        >
                          Permit Number *
                        </label>
                        <input
                          type="text"
                          name="permitNumber"
                          value={checkinOrderData.permitNumber}
                          onChange={handleInputChange}
                          required={requireFlag.PermitFlag}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm outline-gray-320 focus:outline-primary outline-gray-300"
                        />
                        {errors.permitNumber && (
                          <p className="text-red-500 text-xs mt-1">
                            {errors.permitNumber}
                          </p>
                        )}
                      </div>
                    )}
                    {requireFlag.citesFlag && (
                      <div>
                        <label
                          htmlFor="citesNumber"
                          className="block text-sm font-medium text-[#757575] mb-2"
                        >
                          CITES Number *
                        </label>
                        <input
                          type="text"
                          name="citesNumber"
                          value={checkinOrderData.citesNumber}
                          onChange={handleInputChange}
                          required={requireFlag.citesFlag}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm outline-gray-320 focus:outline-primary outline-gray-300"
                        />
                        {errors.citesNumber && (
                          <p className="text-red-500 text-xs mt-1">
                            {errors.citesNumber}
                          </p>
                        )}
                      </div>
                    )}
                    {requireFlag.PermitFlag && (
                      <div>
                        <label
                          htmlFor="conformationNumber"
                          className="block text-sm font-medium text-[#757575] mb-2"
                        >
                          Confirmation Number *
                        </label>
                        <input
                          type="text"
                          name="conformationNumber"
                          value={checkinOrderData.conformationNumber}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm outline-gray-320 focus:outline-primary outline-gray-300"
                        />
                        {errors.conformationNumber && (
                          <p className="text-red-500 text-xs mt-1">
                            {errors.conformationNumber}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {/* Length, Weight and Notes */}
              <div className="border border-b-gray-400 p-4 rounded-lg shadow-md bg-[#FFFEFA]">
                <div className="grid grid-cols-2 sm:grid-cols-2 gap-4 mt-2">
                  {requireFlag.LengthFlag && (
                    <div>
                      <label
                        htmlFor="length"
                        className="block text-sm font-medium text-[#757575] mb-2"
                      >
                        Length *
                      </label>
                      <div className="flex gap-2">
                        {/* Feet Input */}
                        <input
                          type="text"
                          name="feet"
                          value={feetValidation.value ?? checkinOrderData.feet}
                          onChange={handleInputChange}
                          onKeyPress={feetValidation.handleKeyPress}
                          placeholder="0"
                          className="w-1/2 px-3 py-2 border border-gray-300 rounded-md text-sm outline-gray-320 focus:outline-primary outline-gray-300"
                        />
                        <span className="flex items-center text-gray-500 text-sm">
                          Feet
                        </span>
                        <input
                          type="text"
                          name="inches"
                          value={
                            inchesValidation.value ?? checkinOrderData.inches
                          }
                          onChange={handleInputChange}
                          onKeyPress={inchesValidation.handleKeyPress}
                          placeholder="0"
                          className="w-1/2 px-3 py-2 border border-gray-300 rounded-md text-sm outline-gray-320 focus:outline-primary outline-gray-300"
                        />
                        <span className="flex items-center text-gray-500 text-sm">
                          Inches
                        </span>
                      </div>
                      {(errors.feet ||
                        errors.inches ||
                        feetValidation.error ||
                        inchesValidation.error) && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.feet ||
                            errors.inches ||
                            feetValidation.error ||
                            inchesValidation.error}
                        </p>
                      )}
                    </div>
                  )}
                  <div>
                    <label
                      htmlFor="weight"
                      className="block text-sm font-medium text-[#757575] mb-2"
                    >
                      Weight
                    </label>
                    <input
                      type="text"
                      name="weight"
                      value={checkinOrderData.weight}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm outline-gray-320 focus:outline-primary outline-gray-300"
                    />
                    {errors.weight && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.weight}
                      </p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-1 mt-2">
                  <label
                    htmlFor="notes"
                    className="block text-sm font-medium text-[#757575] mb-2"
                  >
                    Notes
                  </label>
                  <textarea
                    name="notes"
                    placeholder="Add notes or description..."
                    value={checkinOrderData.notes}
                    onChange={handleInputChange}
                    className=" block w-full border border-gray-300 rounded-md p-2 mt-1 text-sm focus:outline-primary"
                    rows={3}
                  />
                  {errors.notes && (
                    <p className="text-red-500 text-xs mt-1">{errors.notes}</p>
                  )}
                </div>
              </div>
            </div>
          </form>
          {/* License modal */}
          {showLicenseForm && (
            <LicenseDetails
              onClose={() => {
                setShowLicenseForm(false);
                refreshCustomerDetails();
              }}
            />
          )}
        </div>
        <div className="flex justify-between gap-4 fixed bottom-0 left-14 w-[calc(100%-64px)] p-4">
          <button
            onClick={() =>
              navigate(FrontendRoutes.CREATE_ORDER_SPECIES_CUSTOMISATION)
            }
            type="button"
            className="w-[50%] px-6 py-2 border border-primary rounded-sm text-sm font-medium text-primary bg-white hover:bg-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            Back
          </button>
          <button
            onClick={handleNext}
            type="submit"
            className="w-[50%] px-6 py-2 border border-transparent rounded-sm text-sm font-medium text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary flex items-center justify-center space-x-2"
          >
            <span>Next</span>
          </button>
        </div>
      </div>
    </div>
  );
};